import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import Select from 'react-select';

import { ModalActions } from "redux/actions/ModalActions";
import { SubscriptionActions } from 'redux/actions/settings/SubscriptionActions';
import { TenantActions } from 'redux/actions/settings/TenantActions';

import { TableColumn, Table, TableRow } from 'app/components/common/Table';
import { CstmBtn } from 'app/components/common/Button';
import { PageContentHeader } from 'app/components/common/PageContentHeader';
import Translator from 'app/components/common/Translator';
import { Translate } from 'infrastructure/mapping/Translate';
import { LoginUser } from 'models/LoginUser';
import { StoreModel } from 'models/StoreModel';
import { SubscriptionPlan } from 'models/SubscriptionPlan';
import { Tenant } from 'models/Tenant';
import { Translations } from 'models/Translations';

interface Props {
    fetchSubscriptionPlans: typeof SubscriptionActions.fetchSubscriptionPlans;
    fetchTenants: typeof TenantActions.fetchTenants;
    editSubscriptionPlanForm: typeof ModalActions.showModal;
    editSubscriptionPlan: typeof SubscriptionActions.editSubscriptionPlan;
    setTenantPlan: typeof SubscriptionActions.setTenantPlan;
    loginUser: LoginUser;
    subscriptionPlans: SubscriptionPlan[];
    tenants: Tenant[];
    translations: Translations;
}

interface State {
    // tenantId -> selected subscriptionPlanId, saved with the button
    selectedPlans: { [tenantId: number]: number };
}

class Subscriptions extends React.Component<Props, State> {
    state: State = { selectedPlans: {} };

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.subscriptionPlans) {
                this.props.fetchSubscriptionPlans(this.props.loginUser.token);
            }
            if (!this.props.tenants) {
                this.props.fetchTenants(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.subscriptionPlans && this.props.tenants;
    }

    limit(max: number | null) {
        return max ?? Translate.text(this.props.translations, "Unlimited");
    }

    setPlanData(): TableRow[] {
        return this.props.subscriptionPlans.map(sp => ({
            dataObject: {
                id: sp.id,
                name: sp.name,
                maxMachines: sp.maxMachines,
                maxUsers: sp.maxUsers,
                tasksEnabled: sp.tasksEnabled,
                defectsEnabled: sp.defectsEnabled
            },
            cells: [
                { name: "maxMachines", sortVal: sp.maxMachines ?? Infinity, cell: <>{this.limit(sp.maxMachines)}</> },
                { name: "maxUsers", sortVal: sp.maxUsers ?? Infinity, cell: <>{this.limit(sp.maxUsers)}</> },
                { name: "tasksEnabled", sortVal: sp.tasksEnabled ? 1 : 0, cell: <Translator text={sp.tasksEnabled ? "Yes" : "No"} /> },
                { name: "defectsEnabled", sortVal: sp.defectsEnabled ? 1 : 0, cell: <Translator text={sp.defectsEnabled ? "Yes" : "No"} /> },
            ]
        }));
    }

    setColumns(): TableColumn[] {
        return [
            { header: 'Id', accessor: 'id' },
            { header: 'name', accessor: 'name' },
            { header: 'maxMachines', accessor: 'maxMachines' },
            { header: 'maxUsers', accessor: 'maxUsers' },
            { header: 'tasksEnabled', accessor: 'tasksEnabled' },
            { header: 'defectsEnabled', accessor: 'defectsEnabled' },
        ];
    }

    changedTenants() {
        return this.props.tenants.filter(t => !t.archivedDate && this.state.selectedPlans[t.id] !== undefined && this.state.selectedPlans[t.id] !== t.subscriptionPlan?.id);
    }

    saveTenantPlans() {
        this.changedTenants().forEach(t => this.props.setTenantPlan(this.props.loginUser.token, t.id, this.state.selectedPlans[t.id]));
    }

    tenantsCard() {
        const options = this.props.subscriptionPlans.map(sp => ({ label: sp.name, value: sp }));
        return (
            <div className="c-card">
                <h3><Translator text="Tenants" /></h3>
                <table className="c-table">
                    <thead className="c-table__head">
                        <tr className="c-table__row">
                            <th className="c-table__cell c-table__cell-header"><Translator text="Tenant" /></th>
                            <th className="c-table__cell c-table__cell-header"><Translator text="Subscription" /></th>
                        </tr>
                    </thead>
                    <tbody className="c-table__body">
                        {this.props.tenants.filter(t => !t.archivedDate).map(t => (
                            <tr className="c-table__row c-table__row--striped" key={t.id}>
                                <td className="c-table__cell"><label htmlFor={'tenant.' + t.id}>{t.name}</label></td>
                                <td className="c-table__cell">
                                    <Select
                                        inputId={'tenant.' + t.id}
                                        value={options.find(o => o.value.id === (this.state.selectedPlans[t.id] ?? t.subscriptionPlan?.id)) ?? null}
                                        onChange={o => o && this.setState({ selectedPlans: { ...this.state.selectedPlans, [t.id]: o.value.id } })}
                                        options={options}
                                        placeholder={Translate.text(this.props.translations, "Select...")}
                                        className='c-form__select'
                                        classNamePrefix='c-select'
                                        // render the menu outside the card, which would clip it
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 10 }) }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <CstmBtn onClick={() => this.saveTenantPlans()} text="Save" addClass="c-button--primary" disabled={this.changedTenants().length === 0} />
            </div>
        );
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Subscriptions" />
                    <Translator text="Loading" />
                </div>
            )
        }
        return (
            <div className="c-page__content">
                <PageContentHeader text="Subscriptions" />
                <div className="c-card">
                    <Table
                        // no addAction or deleteAction: the plans are seeded, only editing is allowed
                        addFormData={{
                            header: "Edit Subscription",
                            formObject: SubscriptionPlan.empty(),
                        }}
                        editAction={this.props.editSubscriptionPlan}
                        editFormAction={this.props.editSubscriptionPlanForm}
                        editFormData={{
                            header: "Edit Subscription",
                        }}
                        optionals={['maxMachines', 'maxUsers']}
                        editable={true}
                        columns={this.setColumns()}
                        tableData={this.setPlanData()}
                        translations={this.props.translations}
                        loginUser={this.props.loginUser}
                    />
                </div>
                {this.tenantsCard()}
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        subscriptionPlans: state.subscriptionPlans,
        tenants: state.tenants,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchSubscriptionPlans: bindActionCreators(SubscriptionActions.fetchSubscriptionPlans, dispatch),
        fetchTenants: bindActionCreators(TenantActions.fetchTenants, dispatch),
        editSubscriptionPlanForm: bindActionCreators(ModalActions.showModal, dispatch),
        editSubscriptionPlan: bindActionCreators(SubscriptionActions.editSubscriptionPlan, dispatch),
        setTenantPlan: bindActionCreators(SubscriptionActions.setTenantPlan, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Subscriptions);
